import api from "./api";

export type ExtratoSituacaoApi = {
  id: number;
  nome: string;
};

export type ExtratoSolicitacaoItem = {
  cnpj: string;
  sigla: string;
  solicitacao: string;
  situacao?: ExtratoSituacaoApi | null;
  statusExtrato?: ExtratoSituacaoApi | null;
  situacaoExtrato?: string | null;
  enviadoEm?: string | null;
  dataEnvioExtrato?: string | null;
  atualizadoEm?: string | null;
  dataAtualizacaoExtrato?: string | null;
  idSindicato?: number | null;
  /** Legacy (2025) */
  idQuestionarioResposta?: number | null;
  /** V2 (2026+) */
  idSolicitacaoAvaliacao?: number | null;
  solicitacaoId?: number | null;
};

export type ExtratoModeloItem = {
  anoReferencia: number;
  criadoEm?: string;
  ativo?: boolean;
};

export type ExtratoSolicitacoesResponse = {
  anoReferenciaSelecionado: number;
  avaliacoesModelo?: ExtratoModeloItem[];
  extratosModelo?: ExtratoModeloItem[];
  solicitacoes: ExtratoSolicitacaoItem[];
};

export type ExtratoCriterioBonusItem = {
  criterioId: number;
  titulo: string;
  conforme: boolean;
  percentualBonus: number;
  valor: number;
};

export type ExtratoGrupoCriterioBonus = {
  grupoCriterioId: number;
  titulo: string;
  tituloResumo: string;
  ordem: number;
  criterios: ExtratoCriterioBonusItem[];
  valorAlcancado: number;
};

export type ExtratoTituloEmAbertoItem = {
  id: number;
  recno: number;
  filial: string;
  titulo: string;
  parcela: string;
  natureza: string;
  emitidoEm: string | null;
  vencimentoEm: string | null;
  diasAtraso: number;
  valor: number;
  historico: string;
  deduzir: boolean;
};

export type ExtratoValidacaoSindicato = {
  observacaoSindicato: string;
  respondidoSindicatoEm: string;
};

export type ExtratoSolicitacaoDetalheResponse = {
  solicitacaoAvaliacaoId: number;
  validacaoSindicato: ExtratoValidacaoSindicato | null;
  dadosSindicato: {
    nome: string;
    sigla: string;
    cnpj: string;
  };
  receitaOperacionalExtrato: {
    valorReceitaOperacional: number;
    faixaReceitaBonusExtrato: {
      titulo: string;
      receitaMinima: number;
      receitaMaxima: number;
      valorMinimo: number;
      bonusPotencial: number;
      valorMaximoPotencial: number;
    };
  };
  gruposCriteriosBonusExtrato: ExtratoGrupoCriterioBonus[];
  valorMinimoApoio: number;
  valorTotalApoio: number;
  deducoesExtrato: {
    deducaoAtrasoEntrega: {
      id?: number;
      documentacaoEnviadaEm: string | null;
      dataLimiteEntrega: string | null;
      diasAtraso: number;
      percentualDeducao: number;
      valorTotalApoio: number;
      valorDeducao: number;
      deduzir: boolean;
    };
    deducaoTitulosEmAberto: {
      titulos: ExtratoTituloEmAbertoItem[];
      valorTotalDeducao: number;
    };
    deducaoOutros: {
      deducoes: Array<{
        id: number;
        descricao: string;
        valor: number;
        deduzir: boolean;
      }>;
      valorTotalDeducao: number;
    };
  };
  valorTotalDeducoes: number;
  valorLiquidoApoio: number;
  valoresJaSolicitadosExtrato: {
    solicitacoes: Array<{
      solicitacao: string;
      solicitadoEm: string | null;
      valorApoiado: number;
    }>;
    valorTotalSolicitado: number;
  } | null;
};

export type ExtratoEventoItem = {
  id?: number;
  nome?: string;
  criadoEm?: string;
  arquivoId?: number | null;
};

const parseObjectResponse = <T>(payload: unknown): T | null => {
  if (payload && typeof payload === "object") {
    return payload as T;
  }

  if (typeof payload === "string") {
    try {
      return JSON.parse(payload) as T;
    } catch (error) {
      console.error("❌ Erro ao fazer parse da resposta de extrato:", error);
      return null;
    }
  }

  return null;
};

// Lista extratos legacy (2025) — apenas registros reais do banco
// Endpoint: GET /api/extratos-avaliacoes/legacy
export const getExtratoSolicitacoesLegacy =
  async (): Promise<ExtratoSolicitacoesResponse | null> => {
    try {
      const response = await api.get(
        "/api/extratos-avaliacoes/legacy",
      );
      return parseObjectResponse<ExtratoSolicitacoesResponse>(response?.data);
    } catch (error) {
      console.error("❌ Erro ao buscar extratos legacy:", error);
      throw error;
    }
  };

// Lista extratos por ano de referência (2026+) — inclui virtualização no backend
// Endpoint: GET /api/extratos-avaliacoes/solicitacoes?anoReferencia=ANO
export const getExtratoSolicitacoesByAno = async (
  anoReferencia: number,
): Promise<ExtratoSolicitacoesResponse | null> => {
  try {
    const response = await api.get(
      "/api/extratos-avaliacoes/solicitacoes",
      { params: { anoReferencia } },
    );
    return parseObjectResponse<ExtratoSolicitacoesResponse>(response?.data);
  } catch (error) {
    console.error("❌ Erro ao buscar solicitações de extrato por ano:", error);
    throw error;
  }
};

// Detalhe do extrato por solicitação de avaliação (2026+)
// Endpoint: GET /api/extratos-avaliacoes/solicitacoes/{solicitacaoAvaliacaoId}
export const getExtratoSolicitacaoById = async (
  solicitacaoAvaliacaoId: number | string,
): Promise<ExtratoSolicitacaoDetalheResponse | null> => {
  try {
    const response = await api.get(
      `/api/extratos-avaliacoes/solicitacoes/${solicitacaoAvaliacaoId}`,
    );
    return parseObjectResponse<ExtratoSolicitacaoDetalheResponse>(
      response?.data,
    );
  } catch (error) {
    console.error("❌ Erro ao buscar extrato por solicitação:", error);
    throw error;
  }
};

// Eventos/histórico do extrato por solicitação (2026+)
// Endpoint: GET /api/extratos-avaliacoes/{solicitacaoAvaliacaoId}/eventos
export const getExtratoEventosBySolicitacao = async (
  solicitacaoAvaliacaoId: number | string,
): Promise<ExtratoEventoItem[]> => {
  try {
    const response = await api.get(
      `/api/extratos-avaliacoes/solicitacoes/${solicitacaoAvaliacaoId}/eventos`,
    );
    const payload = response?.data;
    if (Array.isArray(payload)) return payload as ExtratoEventoItem[];
    if (payload && typeof payload === "object") {
      const eventos =
        (payload as { eventos?: ExtratoEventoItem[] }).eventos ??
        (payload as { data?: ExtratoEventoItem[] }).data;
      if (Array.isArray(eventos)) return eventos;
    }
    return [];
  } catch (error) {
    console.error("❌ Erro ao buscar eventos do extrato:", error);
    return [];
  }
};

// Histórico de ações do extrato por solicitação (legado)
// Endpoint: GET /api/extrato/getHistoricoAcaoExtratoByIdSolicitacao/{idSolicitacao}
export const getHistoricoAcaoExtratoByIdSolicitacao = async (
  idSolicitacao: number | string,
) => {
  try {
    const response = await api.get(
      `/api/extrato/getHistoricoAcaoExtratoByIdSolicitacao/${idSolicitacao}`,
    );

    if (
      response?.data?.historicoAcaoExtrato &&
      Array.isArray(response.data.historicoAcaoExtrato)
    ) {
      return response.data;
    }

    return { historicoAcaoExtrato: [] };
  } catch (e) {
    console.error("Erro ao buscar histórico do extrato por solicitação:", e);
    return { historicoAcaoExtrato: [] };
  }
};

// Baixa um extrato pelo identificador do blob
// Endpoint: GET /api/extrato/downloadByIdentificador/{identificadorBlob}
export const downloadExtratoById = async (identificadorBlob) => {
  try {
    const id = String(identificadorBlob || "").trim();

    if (!id) {
      console.warn("identificadorBlob não informado ao baixar extrato.");
      return null;
    }

    const resp = await api.get(
      `/api/extrato/downloadByIdentificador/${encodeURIComponent(id)}`,
      {
        responseType: "blob",
        headers: { Accept: "application/pdf" },
        validateStatus: (s) => s < 500 && s !== 401,
      },
    );

    if (resp.status !== 200 || !(resp.data instanceof Blob)) {
      console.warn(
        "Extrato não encontrado ou resposta inválida.",
        resp?.status,
      );
      return null;
    }

    const contentType = String(
      resp.headers?.["content-type"] || "",
    ).toLowerCase();

    const blob = contentType.includes("pdf")
      ? resp.data
      : new Blob([resp.data], { type: "application/pdf" });

    const url = URL.createObjectURL(blob);

    const cd = resp.headers?.["content-disposition"] || "";
    let filename = `extrato_${id}.pdf`;

    const match = /filename\*?=([^;]+)/i.exec(cd);
    if (match && match[1]) {
      filename = decodeURIComponent(
        match[1]
          .replace(/UTF-8''/i, "")
          .replace(/["']/g, "")
          .trim(),
      );
    }

    return { url, filename, blob };
  } catch (e) {
    console.error("Erro ao baixar extrato:", e);
    return null;
  }
};

// Baixa um arquivo de extrato pelo arquivoId (V2 2026+)
// Endpoint: GET /api/extratos-avaliacoes/solicitacoes/arquivo-evento/{arquivoId}
export const downloadExtratoByArquivoId = async (
  arquivoId: number | string,
): Promise<{ url: string; filename: string; blob: Blob } | null> => {
  try {
    const resp = await api.get(
      `/api/extratos-avaliacoes/solicitacoes/arquivo-evento/${arquivoId}`,
      {
        responseType: "blob",
        headers: { Accept: "application/pdf" },
        validateStatus: (s) => s < 500 && s !== 401,
      },
    );

    if (resp.status !== 200 || !(resp.data instanceof Blob)) {
      console.warn("Arquivo de extrato não encontrado.", resp?.status);
      return null;
    }

    const contentType = String(resp.headers?.["content-type"] || "").toLowerCase();
    const blob = contentType.includes("pdf")
      ? resp.data
      : new Blob([resp.data], { type: "application/pdf" });

    const url = URL.createObjectURL(blob);

    const cd = resp.headers?.["content-disposition"] || "";
    let filename = `extrato_${arquivoId}.pdf`;
    const match = /filename\*?=([^;]+)/i.exec(cd);
    if (match && match[1]) {
      filename = decodeURIComponent(
        match[1].replace(/UTF-8''/i, "").replace(/["']/g, "").trim(),
      );
    }

    return { url, filename, blob };
  } catch (e) {
    console.error("Erro ao baixar arquivo de extrato:", e);
    return null;
  }
};

// Baixa um extrato por id do extrato e data de upload
// Endpoint: POST /api/extrato/downloadExtratoByIdDataUpload
export const downloadExtratoByIdDataUpload = async ({
  idExtrato,
  dataUpload,
}) => {
  try {
    let resp = await api.post(
      "/api/extrato/downloadExtratoByIdDataUpload",
      { idExtrato: Number(idExtrato), dataUpload },
      {
        responseType: "blob",
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*",
        },
        validateStatus: (s) => s < 500 && s !== 401,
      },
    );

    if (resp.status === 200 && resp.data instanceof Blob) {
      const contentType = String(
        resp.headers?.["content-type"] || "",
      ).toLowerCase();
      const blob = contentType.includes("pdf")
        ? resp.data
        : new Blob([resp.data], { type: "application/pdf" });

      const url = URL.createObjectURL(blob);

      const cd = resp.headers?.["content-disposition"] || "";
      let filename = `extrato_${idExtrato}.pdf`;
      const match = /filename\*?=([^;]+)/i.exec(cd);
      if (match && match[1]) {
        filename = decodeURIComponent(
          match[1]
            .replace(/UTF-8''/i, "")
            .replace(/["']/g, "")
            .trim(),
        );
      }

      return { url, filename, blob };
    }

    resp = await api.post(
      "/api/extrato/downloadExtratoByIdDataUpload",
      { idExtrato: Number(idExtrato), dataUpload },
      {
        responseType: "text",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/plain",
        },
        validateStatus: (s) => s < 500 && s !== 401,
      },
    );

    if (
      resp.status !== 200 ||
      typeof resp.data !== "string" ||
      !resp.data.trim()
    ) {
      console.warn(
        "Extrato não encontrado ou resposta inválida:",
        resp?.status,
      );
      return null;
    }

    const urlStr = resp.data.trim();
    const fetchResp = await fetch(urlStr);
    const blob = await fetchResp.blob();
    const url = URL.createObjectURL(blob);

    return { url, filename: `extrato_${idExtrato}.pdf`, blob };
  } catch (e) {
    console.error("Erro ao baixar extrato (id+dataUpload):", e);
    return null;
  }
};

// Baixa o último extrato enviado de um sindicato
// Endpoint: GET /api/extrato/downloadLastSentByIdSindicato/{idSindicato}
export const downloadLastSentByIdSindicato = async (idSindicato) => {
  try {
    const resp = await api.get(
      `/api/extrato/downloadLastSentByIdSindicato/${idSindicato}`,
      {
        responseType: "blob",
        headers: { Accept: "application/pdf" },
        validateStatus: (s) => s < 500 && s !== 401,
      },
    );

    if (resp.status !== 200 || !(resp.data instanceof Blob)) {
      console.warn(
        "Extrato não encontrado ou resposta inválida.",
        resp?.status,
      );
      return null;
    }

    const contentType = String(
      resp.headers?.["content-type"] || "",
    ).toLowerCase();
    const blob = contentType.includes("pdf")
      ? resp.data
      : new Blob([resp.data], { type: "application/pdf" });

    const url = URL.createObjectURL(blob);

    const cd = resp.headers?.["content-disposition"] || "";
    let filename = `extrato_${idSindicato}.pdf`;
    const match = /filename\*?=([^;]+)/i.exec(cd);
    if (match && match[1]) {
      filename = decodeURIComponent(
        match[1]
          .replace(/UTF-8''/i, "")
          .replace(/["']/g, "")
          .trim(),
      );
    }

    return { url, filename, blob };
  } catch (e) {
    console.error("Erro ao baixar extrato:", e);
    return null;
  }
};

// Busca o histórico de ações do extrato por sindicato
// Endpoint: GET /api/extrato/getHistoricoAcaoExtratoByIdSindicato/{idSindicato}
export const getHistoricoAcaoExtrato = async (idSindicato) => {
  try {
    const response = await api.get(
      `/api/extrato/getHistoricoAcaoExtratoByIdSindicato/${idSindicato}`,
    );

    if (
      response?.data?.historicoAcaoExtrato &&
      Array.isArray(response.data.historicoAcaoExtrato)
    ) {
      return response.data;
    }

    return { historicoAcaoExtrato: [] };
  } catch (e) {
    console.error("Erro ao buscar histórico Acao do Extrato:", e);
    return { historicoAcaoExtrato: [] };
  }
};

// Monta o preview do extrato com valores, bônus e demais dados
// Endpoint: POST /api/extrato/buildPreviewExtrato
export const buildPreviewExtrato = async (payload) => {
  try {
    const response = await api.post(
      "/api/extrato/buildPreviewExtrato",
      payload,
    );
    return response?.data ?? {};
  } catch (e) {
    console.error("Erro ao gerar o preview do extrato:", e);
    return {};
  }
};

// Busca as deduções do extrato por sindicato
// Endpoint: GET /api/extrato/getDeducoesExtrato/{idSindicato}
export const getDeducoesExtrato = async (idSindicato) => {
  try {
    const response = await api.get(
      `/api/extrato/getDeducoesExtrato/${idSindicato}`,
    );
    return response?.data ?? {};
  } catch (e) {
    console.error("Erro ao buscar deduções do extrato:", e);
    return {};
  }
};

// Salva os dados do extrato (legacy)
// Endpoint: PATCH /api/extrato/salvarExtrato
export const salvarExtrato = async (payload) => {
  try {
    const response = await api.patch("/api/extrato/salvarExtrato", payload);

    if (response?.status === 200 || response?.status === 204) {
      return true;
    }

    console.warn("Falha ao salvar extrato:", response);
    return false;
  } catch (e) {
    console.error("Erro ao salvar extrato:", e);
    return false;
  }
};

export type PatchExtratoAnalisePayload = {
  deducaoAtraso?: {
    deduzir: boolean;
  };
  deducaoTitulos: Array<{
    id?: number;
    recno?: number;
    filial: string;
    titulo: string;
    parcela: string;
    natureza: string;
    vencimento: string;
    valor: number;
    deduzir: boolean;
  }>;
  deducaoOutros: Array<{
    id?: number;
    descricao: string;
    valor: number;
    deduzir: boolean;
  }>;
};

// Salva deduções do extrato V2 (2026+)
// Endpoint: PATCH /api/extratos-avaliacoes/solicitacoes/{solicitacaoAvaliacaoId}/analise
export const salvarExtratoAnalise = async (
  solicitacaoAvaliacaoId: number | string,
  payload: PatchExtratoAnalisePayload,
): Promise<boolean> => {
  try {
    const response = await api.patch(
      `/api/extratos-avaliacoes/solicitacoes/${solicitacaoAvaliacaoId}/analise`,
      payload,
    );

    if (response?.status === 200 || response?.status === 204) {
      return true;
    }

    console.warn("Falha ao salvar análise do extrato:", response);
    return false;
  } catch (e) {
    console.error("Erro ao salvar análise do extrato:", e);
    return false;
  }
};

// Envia o extrato legacy (2025) com arquivo em multipart/form-data
// Endpoint: POST /api/extrato/enviarExtrato
export const enviarExtrato = async ({
  idSindicato,
  arquivo,
}: {
  idSindicato: number | string;
  arquivo: File | Blob;
}) => {
  const formData = new FormData();
  formData.append("IdSindicato", String(idSindicato));
  formData.append("Arquivo", arquivo, "extrato.pdf");

  try {
    const response = await api.post("/api/extrato/enviarExtrato", formData, {
      headers: { "Content-Type": "multipart/form-data", Accept: "*/*" },
    });

    if (response?.status === 200 || response?.status === 204) {
      return true;
    }

    console.warn("Falha ao enviar extrato:", response);
    return false;
  } catch (error) {
    console.error("Erro ao enviar extrato:", error);
    return false;
  }
};

export type EnviarExtratoSolicitacaoPayload = {
  solicitacaoAvaliacaoId: number | string;
  arquivo: File | Blob;
  pdfResumo: File | Blob;
};

export const enviarExtratoSolicitacao = async ({
  solicitacaoAvaliacaoId,
  arquivo,
  pdfResumo,
}: EnviarExtratoSolicitacaoPayload): Promise<boolean> => {
  const formData = new FormData();
  formData.append("Arquivo", arquivo, "extrato.pdf");
  formData.append("PdfResumo", pdfResumo, "PdfResumo.pdf");

  try {
    const response = await api.post(
      `/api/extratos-avaliacoes/solicitacoes/${solicitacaoAvaliacaoId}/enviar`,
      formData,
      { headers: { "Content-Type": "multipart/form-data", Accept: "*/*" } },
    );

    if (response?.status === 200 || response?.status === 204) {
      return true;
    }

    console.warn("Falha ao enviar extrato da solicitação:", response);
    return false;
  } catch (error) {
    console.error("Erro ao enviar extrato da solicitação:", error);
    return false;
  }
};

// Envia o extrato V2 (2026+) — endpoint /envio, apenas PdfResumo
// Endpoint: POST /api/extratos-avaliacoes/solicitacoes/{solicitacaoAvaliacaoId}/envio
export const enviarExtratoSolicitacaoV2 = async (
  solicitacaoAvaliacaoId: number | string,
  pdfResumo: Blob,
): Promise<boolean> => {
  const formData = new FormData();
  formData.append("PdfResumo", pdfResumo, "PdfResumo.pdf");

  try {
    const response = await api.post(
      `/api/extratos-avaliacoes/solicitacoes/${solicitacaoAvaliacaoId}/envio`,
      formData,
      { headers: { "Content-Type": "multipart/form-data", Accept: "*/*" } },
    );

    if (response?.status === 200 || response?.status === 204) {
      return true;
    }

    console.warn("Falha ao enviar extrato V2:", response);
    return false;
  } catch (error) {
    console.error("Erro ao enviar extrato V2:", error);
    return false;
  }
};

// Endpoint: POST /api/extratos-avaliacoes/solicitacoes/{solicitacaoAvaliacaoId}/validacao
export const postExtratoValidacao = async (
  solicitacaoAvaliacaoId: number | string,
  payload: unknown,
): Promise<boolean> => {
  try {
    const response = await api.post(
      `/api/extratos-avaliacoes/solicitacoes/${solicitacaoAvaliacaoId}/validacao`,
      payload,
    );
    return response?.status === 200 || response?.status === 204;
  } catch (error) {
    console.error("Erro ao validar extrato:", error);
    return false;
  }
};

// Endpoint: POST /api/extratos-avaliacoes/solicitacoes/{solicitacaoAvaliacaoId}/retorno-solicitacao-avaliacao
export const postExtratoRetornoSolicitacao = async (
  solicitacaoAvaliacaoId: number | string,
): Promise<boolean> => {
  try {
    const response = await api.post(
      `/api/extratos-avaliacoes/solicitacoes/${solicitacaoAvaliacaoId}/retorno-solicitacao-avaliacao`,
    );
    return response?.status === 200 || response?.status === 204;
  } catch (error) {
    console.error("Erro ao retornar solicitação de avaliação:", error);
    return false;
  }
};
